import { useEffect, useRef } from 'react';
import type { Socket } from 'socket.io-client';
import { WS_EVENTS, type WsEventPayloads } from '@supporchat/shared';
import { createSocket } from '../lib/socket';

type Handlers = Partial<{
  [E in keyof WsEventPayloads]: (payload: WsEventPayloads[E]) => void;
}>;

/**
 * Mantém uma única conexão de WebSocket e reencaminha os eventos para os
 * handlers atuais.
 *
 * Os handlers ficam numa ref para que mudar um callback (que muda a cada
 * render) não derrube e reabra a conexão — o socket só é recriado quando o
 * token muda.
 */
export function useRealtime(token: string | null, handlers: Handlers): void {
  const handlersRef = useRef<Handlers>(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    if (!token) return;

    const socket: Socket = createSocket(token);

    for (const event of Object.values(WS_EVENTS)) {
      socket.on(event, (payload: unknown) => {
        const handler = handlersRef.current[event as keyof WsEventPayloads];
        if (handler) {
          (handler as (value: unknown) => void)(payload);
        }
      });
    }

    return () => {
      socket.removeAllListeners();
      socket.disconnect();
    };
  }, [token]);
}
